import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import './Admin.css';

const BACKEND_URL = 'https://api.rakaba.io';
// const ADMIN_USER_ID = 'user_3GVzFY8NtOQbb1ji2HwJAdw4fnu';

function RakabaLogo({ size = 28 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 36 36" fill="none">
      <ellipse cx="13" cy="13" rx="7" ry="10" transform="rotate(-45 13 13)" fill="#10b981" opacity="0.95"/>
      <ellipse cx="23" cy="13" rx="7" ry="10" transform="rotate(45 23 13)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="13" cy="23" rx="7" ry="10" transform="rotate(45 13 23)" fill="#10b981" opacity="0.75"/>
      <ellipse cx="23" cy="23" rx="7" ry="10" transform="rotate(-45 23 23)" fill="#10b981" opacity="0.55"/>
      <circle cx="18" cy="18" r="3.5" fill="#052e16"/>
    </svg>
  );
}

export default function Admin() {
  const { getToken } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  useEffect(() => {
    fetchStats();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchStats() {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-API-Key': process.env.REACT_APP_API_KEY
        }
      });
      if (res.status === 403) {
        setError('Access denied. Admin only.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setStats(data.stats);
        setLastRefresh(new Date());
      }
    } catch (err) {
      setError('Failed to load stats: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return (
    <div className="admin-loading">
      <RakabaLogo size={40}/>
      <div className="admin-loading-text">Loading dashboard...</div>
    </div>
  );

  if (error) return (
    <div className="admin-error">
      <div className="admin-error-icon">⚠️</div>
      <div>{error}</div>
      <a href="/leads" className="admin-back-btn">← Back to app</a>
    </div>
  );

  const { totalUsers, planBreakdown, totalLeads, leadsThisMonth,
          topCompanies, activeUsers, recentSignups, hotRate, month } = stats;

  const paidUsers = (planBreakdown.pro || 0) + (planBreakdown.team || 0) + (planBreakdown.agency || 0);
  const mrr = (planBreakdown.pro || 0) * 29 + (planBreakdown.team || 0) * 79 + (planBreakdown.agency || 0) * 199;

  return (
    <div className="admin-root">
      {/* Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <RakabaLogo size={26}/>
          <span className="admin-title">Rakaba</span>
          <span className="admin-divider">/</span>
          <span className="admin-subtitle">Admin</span>
        </div>
        <div className="admin-header-right">
          <span className="admin-month">{month}</span>
          <button className="admin-refresh-btn" onClick={fetchStats}>↻ Refresh</button>
          <a href="/leads" className="admin-app-btn">← App</a>
        </div>
      </header>

      <div className="admin-body">

        {/* KPI Cards */}
        <div className="admin-kpi-grid">
          <div className="admin-kpi">
            <div className="admin-kpi-label">MRR</div>
            <div className="admin-kpi-val admin-kpi-green">${mrr}</div>
            <div className="admin-kpi-sub">{paidUsers} paying {paidUsers === 1 ? 'customer' : 'customers'}</div>
          </div>
          <div className="admin-kpi">
            <div className="admin-kpi-label">Total Users</div>
            <div className="admin-kpi-val">{totalUsers}</div>
            <div className="admin-kpi-sub">{recentSignups?.length || 0} new this week</div>
          </div>
          <div className="admin-kpi">
            <div className="admin-kpi-label">Leads This Month</div>
            <div className="admin-kpi-val">{leadsThisMonth}</div>
            <div className="admin-kpi-sub">{totalLeads} all time</div>
          </div>
          <div className="admin-kpi">
            <div className="admin-kpi-label">HOT Lead Rate</div>
            <div className="admin-kpi-val admin-kpi-red">{hotRate}%</div>
            <div className="admin-kpi-sub">of all researched leads</div>
          </div>
        </div>

        <div className="admin-grid">

          {/* Plan Breakdown */}
          <div className="admin-card">
            <div className="admin-card-title">Plan Breakdown</div>
            <div className="admin-plan-list">
              {[
                { plan: 'free', color: '#4a5568', price: '$0' },
                { plan: 'pro', color: '#10b981', price: '$29' },
                { plan: 'team', color: '#3b82f6', price: '$79' },
                { plan: 'agency', color: '#f59e0b', price: '$199' },
              ].map(({ plan, color, price }) => (
                <div key={plan} className="admin-plan-row">
                  <div className="admin-plan-dot" style={{ background: color }}/>
                  <span className="admin-plan-name">{plan}</span>
                  <span className="admin-plan-price">{price}/mo</span>
                  <div className="admin-plan-bar-wrap">
                    <div className="admin-plan-bar-fill" style={{
                      width: totalUsers ? `${((planBreakdown[plan] || 0) / totalUsers) * 100}%` : '0%',
                      background: color
                    }}/>
                  </div>
                  <span className="admin-plan-count">{planBreakdown[plan] || 0}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Top Companies */}
          <div className="admin-card">
            <div className="admin-card-title">Most Researched Companies</div>
            <div className="admin-company-list">
              {topCompanies?.length === 0 && <div className="admin-empty">No data yet</div>}
              {topCompanies?.map((c, i) => (
                <div key={i} className="admin-company-row">
                  <span className="admin-company-rank">#{i + 1}</span>
                  <span className="admin-company-name">{c.name}</span>
                  <div className="admin-company-bar-wrap">
                    <div className="admin-company-bar-fill" style={{
                      width: topCompanies[0]?.count ? `${(c.count / topCompanies[0].count) * 100}%` : '0%'
                    }}/>
                  </div>
                  <span className="admin-company-count">{c.count}x</span>
                </div>
              ))}
            </div>
          </div>

          {/* Active Users */}
          <div className="admin-card admin-card-wide">
            <div className="admin-card-title">Users</div>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Plan</th>
                  <th>Leads this month</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {activeUsers?.map((u, i) => (
                  <tr key={i}>
                    <td className="admin-td-email">{u.email}</td>
                    <td>
                      <span className={`admin-plan-badge admin-plan-${u.plan}`}>
                        {u.plan}
                      </span>
                    </td>
                    <td className="admin-td-leads">
                      <span className="admin-leads-num">{u.leadsThisMonth}</span>
                    </td>
                    <td className="admin-td-date">
                      {new Date(u.joined).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Recent Signups */}
          <div className="admin-card">
            <div className="admin-card-title">Recent Signups (7 days)</div>
            {recentSignups?.length === 0 ? (
              <div className="admin-empty">No new signups this week</div>
            ) : (
              <div className="admin-signup-list">
                {recentSignups?.map((u, i) => (
                  <div key={i} className="admin-signup-row">
                    <div className="admin-signup-avatar">
                      {u.email[0].toUpperCase()}
                    </div>
                    <div>
                      <div className="admin-signup-email">{u.email}</div>
                      <div className="admin-signup-date">
                        {new Date(u.joined).toLocaleDateString()} · {u.plan}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* System Status */}
          <div className="admin-card">
            <div className="admin-card-title">System Status</div>
            <div className="admin-status-list">
              {[
                { name: 'API', url: 'https://api.rakaba.io/health', key: 'api' },
                { name: 'App', url: 'https://app.rakaba.io/leads', key: 'app' },
                { name: 'Landing', url: 'https://rakaba.io', key: 'landing' },
              ].map(({ name, url, key }) => (
                <div key={key} className="admin-status-row">
                  <div className="admin-status-dot admin-status-green"/>
                  <span className="admin-status-name">{name}</span>
                  <a href={url} target="_blank" rel="noreferrer" className="admin-status-url">
                    {url.replace('https://', '')} ↗
                  </a>
                </div>
              ))}
              <div className="admin-status-row">
                <div className="admin-status-dot admin-status-green"/>
                <span className="admin-status-name">Supabase</span>
                <span className="admin-status-url">{totalUsers} users in DB</span>
              </div>
            </div>
            {lastRefresh && (
              <div className="admin-last-refresh">
                Last refreshed: {lastRefresh.toLocaleTimeString()}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
